import type { Vec } from '@kanvas/board-core';
import type { BoardDoc } from '../collab/BoardDoc';
import type { Identity } from '../lib/identity';

export interface Mods {
  shift: boolean;
  alt: boolean;
  /** Ctrl on Windows/Linux, Cmd on macOS: the "primary" modifier. */
  primary: boolean;
  /** Held space, which turns any tool into a temporary hand tool. */
  space: boolean;
  /** Right button or ctrl-click on macOS. */
  secondary: boolean;
}

export interface PointerInfo {
  /** Board coordinates. */
  board: Vec;
  /** Screen coordinates in CSS pixels, relative to the canvas element. */
  screen: Vec;
  pressure: number;
  pointerType: 'mouse' | 'pen' | 'touch';
}

export interface GestureContext {
  doc: BoardDoc;
  identity: Identity;
  pointer: PointerInfo;
  mods: Mods;
}

/**
 * A pointer gesture, created on pointerdown and owned until pointerup.
 *
 * Modelling interactions as objects rather than a flag soup in the event
 * handler keeps each behaviour self-contained: the gesture captures whatever
 * start state it needs, and the canvas only has to forward move/up/cancel.
 */
export interface Gesture {
  /** CSS cursor to apply while the gesture is active. */
  readonly cursor?: string;
  /**
   * Whether the gesture wants every sample the browser folded into one move
   * event, instead of only the latest position.
   *
   * A pen samples several times faster than the browser dispatches pointermove,
   * and keeping only the last sample of each batch is exactly what turns a fast
   * stroke into a polygon. Ink therefore asks for the lot. A drag does not: the
   * result depends only on where the pointer ended up, so replaying the samples
   * in between would multiply the work per frame for an identical picture.
   */
  readonly coalesce?: boolean;
  move(ctx: GestureContext): void;
  up(ctx: GestureContext): void;
  /** Escape, or the pointer being cancelled by the browser. */
  cancel(ctx: GestureContext): void;
}

/** Distance in screen pixels before a press is treated as a drag. */
export const DRAG_THRESHOLD = 3;

/** Snap threshold in screen pixels; divided by zoom at the call site. */
export const SNAP_THRESHOLD_PX = 7;
